import React, { useEffect, useState } from "react";
import { Navigate, Route, BrowserRouter as Router, Routes, useNavigate } from "react-router-dom";
import "./App.css";

// Hooks
import { AuthProvider, useAuth } from "./hooks/useAuth";

// Pages
import AdminPage from "./pages/AdminPage";
import AdminSelectionPage from "./pages/AdminSelectionPage";
import CertificateSuccessPage from "./pages/CertificateSuccessPage";
import CertificateVerificationPage from "./pages/CertificateVerificationPage";
import CoursePage from "./pages/CoursePage";
import DashboardPage from "./pages/DashboardPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import UserAchievementsPage from "./pages/UserAchievementsPage";

// Admin Pages
import AdminCoursesPage from "./pages/admin/AdminCoursesPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminGroupsPage from "./pages/admin/AdminGroupsPage";
import AdminCertificatesPage from "./pages/admin/AdminCertificatesPage";
import AdminAnalyticsPage from "./pages/admin/AdminAnalyticsPage";
import AdminProgressPage from "./pages/admin/AdminProgressPage";

// Components
import UserDashboard from "./components/admin/UserDashboard";

// Components

// UserDashboard Wrapper
const UserDashboardWrapper = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate("/admin");
  };

  return <UserDashboard user={user} onBack={handleBack} />;
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Main App Component
function AppContent() {
  const { isAuthenticated, isAdmin, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  // Aguardar o user ser carregado do localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100); // Pequeno delay para garantir que o user foi carregado
    return () => clearTimeout(timer);
  }, [user]);

  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" replace />}
        />
        <Route
          path="/register"
          element={!isAuthenticated ? <RegisterPage /> : <Navigate to="/dashboard" replace />}
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/group/:groupId"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/achievements"
          element={
            <ProtectedRoute>
              <UserAchievementsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users/:userId/dashboard"
          element={
            <ProtectedRoute adminOnly={true}>
              <UserDashboardWrapper />
            </ProtectedRoute>
          }
        />

        {/* Admin Section Routes */}
        <Route
          path="/admin/courses"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminCoursesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/groups"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminGroupsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/certificates"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminCertificatesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminAnalyticsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/progress"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminProgressPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Dashboard - must be last */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/course/:courseId"
          element={
            <ProtectedRoute>
              <CoursePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/certificates/courses/:courseId/details/:certId"
          element={
            <ProtectedRoute>
              <CertificateSuccessPage />
            </ProtectedRoute>
          }
        />

        {/* Public Routes */}
        <Route path="/verify-certificate" element={<CertificateVerificationPage />} />

        {/* Default Routes */}
        <Route
          path="/"
          element={
            isLoading ? (
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Carregando...</p>
                </div>
              </div>
            ) : isAuthenticated ? (
              (() => {
                const adminCheck = isAdmin();
                return adminCheck ? <AdminSelectionPage /> : <Navigate to="/dashboard" replace />;
              })()
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Catch all - moved to end to avoid conflicts */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

// Root App Component
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
